import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import AdminLayout from '../../components/AdminLayout';

import Swal from 'sweetalert2';
import {
  Pencil,
  Trash2,
  Plus,
  ChevronRight
} from 'lucide-react';

function ColorSwatch({ color }) {
  return (
    <span
      className="inline-block w-5 h-5 rounded border border-gray-300 align-middle mr-2 flex-shrink-0"
      style={{ backgroundColor: color }}
    />
  );
}

function Templates() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/admin/templates');
      setTemplates(response.data.templates);
      setLoading(false);
    } catch (err) {
      console.error('Fetch templates error:', err);
      setError('Failed to load templates');
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const response = await api.post('/admin/templates', { name: newName.trim() });
      const created = response.data.template;
      setShowAddModal(false);
      setNewName('');
      navigate(`/admin/templates/${created.id}/edit`);
    } catch (err) {
      console.error('Create template error:', err);
      Swal.fire('Error', err.response?.data?.error || 'Failed to create template.', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This template will be permanently deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/templates/${id}`);
        setSuccessMessage('Template deleted successfully.');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
        fetchTemplates();
      } catch (err) {
        console.error('Delete template error:', err);
        Swal.fire('Error', err.response?.data?.error || 'Failed to delete template.', 'error');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Loading templates...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <AdminLayout activeSection="TEMPLATES">
      <style>
        {`
          @keyframes fadeInRight {
            from { opacity: 0; transform: translateX(20px); }
            to { opacity: 1; transform: translateX(0); }
          }
          .animate-fadeInRight { animation: fadeInRight 0.3s ease-out; }
        `}
      </style>

      {showSuccess && (
        <div className="fixed top-4 right-4 z-50 animate-fadeInRight">
          <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
            <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      <h1 className="text-4xl font-bold text-white mb-6">TEMPLATES</h1>

      {/* Add Template Modal */}
      {showAddModal && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && setShowAddModal(false)}
        >
          <div className="relative bg-white rounded-3xl shadow-xl p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-6">New Template</h3>
            <form onSubmit={handleCreate} className="space-y-6">
              <div>
                <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                  Template Name
                </label>
                <input
                  type="text"
                  required
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Enter template name"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); setNewName(''); }}
                  className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-8 py-3 bg-teal-700 text-white rounded-xl hover:bg-teal-600 transition-colors disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create & Configure'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Templates Table */}
      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden" style={{ minHeight: '600px' }}>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-teal-800 text-white">
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">Name</th>
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">Primary</th>
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">Secondary</th>
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">Accent</th>
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">Header Font</th>
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">Paragraph Font</th>
                <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {templates.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-sm text-gray-500">
                    No templates found. Add your first template below.
                  </td>
                </tr>
              ) : (
                templates.map((template) => (
                  <tr key={template.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {template.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex items-center">
                        <ColorSwatch color={template.primary_color} />
                        <span className="font-mono text-xs">{template.primary_color}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex items-center">
                        <ColorSwatch color={template.secondary_color} />
                        <span className="font-mono text-xs">{template.secondary_color}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex items-center">
                        <ColorSwatch color={template.accent_color} />
                        <span className="font-mono text-xs">{template.accent_color}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {template.header_font}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {template.paragraph_font}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => navigate(`/admin/templates/${template.id}/edit`)}
                          className="text-teal-600 hover:text-teal-800"
                          title="Edit"
                        >
                          <Pencil className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(template.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer with Add Button */}
        <div className="px-6 py-4 flex justify-between items-center">
          <button
            onClick={() => setShowAddModal(true)}
            className="w-12 h-12 bg-teal-800 text-white rounded-full flex items-center justify-center hover:bg-teal-700 transition-colors shadow-lg"
            title="Add New Template"
          >
            <Plus className="w-8 h-8" />
          </button>

          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">1/1</span>
            <button className="p-2 text-gray-600 hover:text-teal-700 rounded-full hover:bg-gray-100">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default Templates;
